import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { api } from '../services/api';

const AuthContext = createContext();

const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  loading: false,
  error: null,
  users: [], // For superuser management
  loadingUsers: false
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_LOADING_USERS':
      return { ...state, loadingUsers: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
        error: null
      };
    
    case 'REGISTER_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
        error: null
      };
    
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: null
      };
    
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
        loading: false,
        error: null
      };
    
    case 'SET_USERS':
      return {
        ...state,
        users: action.payload,
        loadingUsers: false,
        error: null
      };
    
    case 'UPDATE_USER_IN_LIST':
      return {
        ...state,
        users: state.users.map(user => 
          user.id === action.payload.id ? action.payload : user
        )
      };
    
    case 'REMOVE_USER_FROM_LIST':
      return {
        ...state,
        users: state.users.filter(user => user.id !== action.payload)
      };
    
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      if (state.token) {
        try {
          const user = await api.getCurrentUser();
          dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token: state.token } });
        } catch (error) {
          console.error('Token validation failed:', error);
          dispatch({ type: 'LOGOUT' });
        }
      }
    };

    checkAuth();
  }, []);

  // Listen for auth errors from API calls
  useEffect(() => {
    const handleAuthError = (event) => {
      console.log('Auth error detected:', event.detail);
      // Show a brief notification to the user
      if (typeof window !== 'undefined' && window.alert) {
        alert('Your session has expired. Please log in again.');
      }
      dispatch({ type: 'LOGOUT' });
    };

    window.addEventListener('authError', handleAuthError);

    return () => {
      window.removeEventListener('authError', handleAuthError);
    };
  }, []);

  // Login function
  const login = async (username, password) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    
    try {
      const response = await api.login(username, password);
      localStorage.setItem('token', response.access_token);
      dispatch({ type: 'LOGIN_SUCCESS', payload: response });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Login failed' });
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Register function
  const register = async (userData) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    
    try {
      const response = await api.register(userData);
      localStorage.setItem('token', response.access_token);
      dispatch({ type: 'REGISTER_SUCCESS', payload: response });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Registration failed' });
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('token');
    dispatch({ type: 'LOGOUT' });
  };

  // Update current user
  const updateCurrentUser = async (userData) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    
    try {
      const updatedUser = await api.updateCurrentUser(userData);
      dispatch({ type: 'UPDATE_USER', payload: updatedUser });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Update failed' });
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Update current user password
  const updateCurrentUserPassword = async (passwordData) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    
    try {
      await api.updateCurrentUserPassword(passwordData);
      dispatch({ type: 'SET_LOADING', payload: false });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Password update failed' });
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Get all users (superuser only)
  const getUsers = useCallback(async () => {
    dispatch({ type: 'SET_LOADING_USERS', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    
    try {
      const users = await api.getUsers();
      dispatch({ type: 'SET_USERS', payload: users });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Failed to fetch users' });
      dispatch({ type: 'SET_LOADING_USERS', payload: false });
    }
  }, []);

  // Update user (superuser only)
  const updateUser = useCallback(async (userId, userData) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    
    try {
      const updatedUser = await api.updateUser(userId, userData);
      dispatch({ type: 'UPDATE_USER_IN_LIST', payload: updatedUser });
      dispatch({ type: 'SET_LOADING', payload: false });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Failed to update user' });
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Delete user (superuser only)
  const deleteUser = useCallback(async (userId) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    
    try {
      await api.deleteUser(userId);
      dispatch({ type: 'REMOVE_USER_FROM_LIST', payload: userId });
      dispatch({ type: 'SET_LOADING', payload: false });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Failed to delete user' });
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  const value = {
    ...state,
    login,
    register,
    logout,
    updateCurrentUser,
    updateCurrentUserPassword,
    getUsers,
    updateUser,
    deleteUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 